import React from 'react';

import {assert, assertAndContinue} from 'Asserts';
import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ExecutionPlan} from 'studies/progress/Types';
import {
  CreationTrayCallbacks,
  OnUnknownErrorAction,
  showCreationTrayTask,
} from 'studies/sidepanel/CreationTrayTask';
import {CreateExperimentsResponse} from 'studies/StudyDAO';
import {navigateToStudyOverviewTab, refreshStudyFolder} from 'studies/StudyNavigationUtils';
import {getProcedureRunUIStringForStudyType} from 'studies/TypedStudyUtils';
import {BasePendingTask} from 'util/resume-persisted-tasks/PersistedTaskTypes';
import * as TaskRunner from 'util/TaskRunner';

import {refreshClinicalBasedStudyOverview, refreshStudyOverview} from './TrayTaskUtils';

export interface CreateExperimentTaskRequestMetadataCreate {
  studyId: string;
  studyType: StudyType;
  // Optional: Included when promoting a procedure run plan so we can restore the promoting state on refresh
  procedureRunPlanId?: string;
}

export interface CreateExperimentTaskRequestMetadata {
  creates: Array<CreateExperimentTaskRequestMetadataCreate>;
}

export type ExperimentCreatePendingTask = BasePendingTask<
  'EXPERIMENT',
  CreateExperimentTaskRequestMetadata,
  CreateExperimentsResponse
>;

export function getExperimentDescriptor(
  creates: Array<CreateExperimentTaskRequestMetadataCreate>
): JSX.Element {
  assert(creates.length > 0, 'Expected at least one create');
  assertAndContinue(
    new Set(creates.map((create) => create.studyType)).size === 1,
    'Expected all creates to have the same studyType'
  );

  if (creates.length > 1) {
    return (
      <>{`${creates.length} ${getProcedureRunUIStringForStudyType(creates[0].studyType, {plural: true})}`}</>
    );
  } else {
    return <>{getProcedureRunUIStringForStudyType(creates[0].studyType)}</>;
  }
}

export function showExperimentCreationTrayTask(
  taskId: string,
  statusUrl: string,
  taskMeta: TaskRunner.TaskMeta,
  creates: Array<CreateExperimentTaskRequestMetadataCreate>,
  callbacks: CreationTrayCallbacks<CreateExperimentsResponse>,
  timingMeta?: ExecutionPlan,
  persistedPendingTask?: ExperimentCreatePendingTask
): void {
  const studyType = creates[0].studyType;
  showCreationTrayTask<'EXPERIMENT', CreateExperimentTaskRequestMetadata, CreateExperimentsResponse>(
    taskId,
    statusUrl,
    taskMeta,
    {
      creation: getExperimentDescriptor(creates),
      creationErrorHeader: `Creating ${getProcedureRunUIStringForStudyType(studyType)} failed.`,
      taskStatusErrorHeader: `Failed to fetch ${getProcedureRunUIStringForStudyType(studyType)} creation progress.`,
    },
    callbacks,
    timingMeta,
    persistedPendingTask
  );
}

export function getExperimentUnknownErrorAction(studyId: string, studyType: StudyType): OnUnknownErrorAction {
  return {
    text: 'View study',
    description: (
      <>
        Click <i>View study</i> to manually verify if the {getProcedureRunUIStringForStudyType(studyType)} was
        created. If not, consider <i>retrying.</i>
      </>
    ),
    onClick: () => {
      navigateToStudyOverviewTab(studyId);
    },
  };
}

export async function onSuccessAfterExperimentCreate(
  studyId: string,
  response: CreateExperimentsResponse,
  studyType: StudyType
): Promise<void> {
  if (studyType === StudyType.CLINICAL || studyType === StudyType.PRECLINICAL) {
    await refreshClinicalBasedStudyOverview(studyId);
  } else {
    await refreshStudyOverview(studyId, studyType);
  }
  const folderIds = new Set<string>(response.creates.map((create) => create.folderId));
  folderIds.forEach((folderId) => {
    refreshStudyFolder(folderId);
  });
}

export function onOpenAfterExperimentCreate(studyId: string, runId?: string): void {
  navigateToStudyOverviewTab(studyId, runId);
}
